import {
  applyEnvironment,
  environments,
  type Environment
} from './environments.ts'
import { hasPlaceholder, safeHref, type MdToken } from './markdown.ts'

interface Counters {
  code: number
  pair: number
  link: number
}

export type InlineNode =
  | { key: string; type: 'text'; text: string }
  | { key: string; type: 'strong'; children: InlineNode[] }
  | { key: string; type: 'em'; children: InlineNode[] }
  | { key: string; type: 'code'; text: string }
  | { key: string; type: 'break' }
  | {
      key: string
      type: 'link'
      testId: string
      href: string
      internal: boolean
      children: InlineNode[]
    }

export interface CellNode {
  key: string
  children: InlineNode[]
}

export interface ListItemNode {
  key: string
  children: BlockNode[]
}

export type BlockNode =
  | { key: string; type: 'heading'; level: 2 | 3 | 4; children: InlineNode[] }
  | { key: string; type: 'paragraph'; children: InlineNode[] }
  | { key: string; type: 'code'; testId: string; text: string }
  | { key: string; type: 'list'; ordered: boolean; items: ListItemNode[] }
  | {
      key: string
      type: 'table'
      header: CellNode[]
      rows: { key: string; cells: CellNode[] }[]
    }
  | { key: string; type: 'rule' }
  | { key: string; type: 'inline'; children: InlineNode[] }
  | { key: string; type: 'text'; text: string }
  | {
      key: string
      type: 'env'
      pair: number
      panels: {
        id: Environment['id']
        label: Environment['label']
        node: BlockNode
      }[]
    }

function textOf(token: MdToken, env?: Environment): string {
  const value = token.text ?? ''
  return env ? applyEnvironment(value, env) : value
}

function headingLevel(depth: number | undefined): 2 | 3 | 4 {
  if (depth === 3) return 3
  if (depth !== undefined && depth >= 4) return 4
  return 2
}

function layInline(
  tokens: readonly MdToken[],
  ids: Counters,
  key: string,
  env?: Environment
): InlineNode[] {
  return tokens.flatMap((token, index) =>
    layInlineToken(token, ids, `${key}-n${index}`, env)
  )
}

function layInlineToken(
  token: MdToken,
  ids: Counters,
  key: string,
  env?: Environment
): InlineNode[] {
  if (token.type === 'strong') {
    return [
      {
        key,
        type: 'strong',
        children: layInline(token.tokens ?? [], ids, key, env)
      }
    ]
  }
  if (token.type === 'em') {
    return [
      {
        key,
        type: 'em',
        children: layInline(token.tokens ?? [], ids, key, env)
      }
    ]
  }
  if (token.type === 'codespan') {
    return [{ key, type: 'code', text: textOf(token, env) }]
  }
  if (token.type === 'br') return [{ key, type: 'break' }]
  if (token.type === 'link') {
    const raw = token.href ?? ''
    const href = safeHref(env ? applyEnvironment(raw, env) : raw)
    const children = layInline(token.tokens ?? [], ids, key, env)
    if (!href) return children
    const testId = `md-link-${ids.link}`
    ids.link += 1
    return [
      {
        key,
        type: 'link',
        testId,
        href,
        internal: href.startsWith('/'),
        children
      }
    ]
  }
  if (token.tokens && token.tokens.length > 0) {
    return layInline(token.tokens, ids, key, env)
  }
  return [{ key, type: 'text', text: textOf(token, env) }]
}

function layCells(
  cells: readonly MdToken[] | undefined,
  ids: Counters,
  key: string,
  env?: Environment
): CellNode[] {
  return (cells ?? []).map((cell, index) => ({
    key: `${key}-c${index}`,
    children: layInline(cell.tokens ?? [], ids, `${key}-c${index}`, env)
  }))
}

function layBlock(
  token: MdToken,
  ids: Counters,
  key: string,
  env?: Environment,
  codeIndex?: number
): BlockNode {
  if (env === undefined && hasPlaceholder(token)) {
    const pair = ids.pair
    ids.pair += 1
    const reserved = token.type === 'code' ? ids.code : undefined
    if (token.type === 'code') ids.code += 1
    return {
      key,
      type: 'env',
      pair,
      panels: environments.map(item => ({
        id: item.id,
        label: item.label,
        node: layBlock(token, ids, `${key}-${item.id}`, item, reserved)
      }))
    }
  }

  if (token.type === 'heading') {
    return {
      key,
      type: 'heading',
      level: headingLevel(token.depth),
      children: layInline(token.tokens ?? [], ids, key, env)
    }
  }
  if (token.type === 'paragraph') {
    return {
      key,
      type: 'paragraph',
      children: layInline(token.tokens ?? [], ids, key, env)
    }
  }
  if (token.type === 'code') {
    const index = codeIndex ?? ids.code
    if (codeIndex === undefined) ids.code += 1
    const testId = env ? `code-copy-${env.id}-${index}` : `code-copy-${index}`
    return { key, type: 'code', testId, text: textOf(token, env) }
  }
  if (token.type === 'list') {
    return {
      key,
      type: 'list',
      ordered: token.ordered === true,
      items: (token.items ?? []).map((item, index) => ({
        key: `${key}-i${index}`,
        children: (item.tokens ?? []).map((child, childIndex) => {
          const childKey = `${key}-i${index}-c${childIndex}`
          if (child.type === 'list') return layBlock(child, ids, childKey, env)
          return {
            key: childKey,
            type: 'inline' as const,
            children: layInline(child.tokens ?? [child], ids, childKey, env)
          }
        })
      }))
    }
  }
  if (token.type === 'table') {
    return {
      key,
      type: 'table',
      header: layCells(token.header, ids, `${key}-h`, env),
      rows: (token.rows ?? []).map((row, index) => ({
        key: `${key}-r${index}`,
        cells: layCells(row, ids, `${key}-r${index}`, env)
      }))
    }
  }
  if (token.type === 'hr') return { key, type: 'rule' }
  if (token.tokens && token.tokens.length > 0) {
    return {
      key,
      type: 'inline',
      children: layInline(token.tokens, ids, key, env)
    }
  }
  return { key, type: 'text', text: textOf(token, env) }
}

export function layOut(tokens: readonly MdToken[]): BlockNode[] {
  const ids: Counters = { code: 0, pair: 0, link: 0 }
  return tokens.map((token, index) => layBlock(token, ids, `b${index}`))
}
